import React, { createContext, useReducer, ReactNode } from 'react';

import NextApiService from 'services/api';
import { IReportGroup } from 'services/reportGroupsService';
import { IReport } from 'services/reportsService';

import reducer, { BIState, BITypes, initialState } from './state/BIState';

interface IActions {
  setScreen: (isMobile?: boolean) => void;
  toggleMenu: () => void;
  setActiveGroup: (reportGroup?: IReportGroup) => void;
  setReport: (report: IReport) => void;
  setAvaliableReportGroups: (reportGroups?: IReportGroup[]) => void;
  getReportConfig: (
    workspaceId: string,
    reportId: string,
    upn?: string,
  ) => void;
}

export const BIContext = createContext<{
  state: BIState;
  actions: IActions;
}>({
  state: initialState,
  actions: {
    setScreen: () => {},
    toggleMenu: () => {},
    setActiveGroup: () => {},
    setReport: () => {},
    setAvaliableReportGroups: () => {},
    getReportConfig: () => {},
  },
});

interface IProps extends BIState {
  children: ReactNode;
}

const api = NextApiService();

const BIContextProvider: React.FC<IProps> = ({ showMenu, children }) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    showMenu,
  });

  /**
   * ACTIONS
   */
  const setScreen = (isMobile?: boolean) => {
    dispatch({ type: BITypes.SET_SCREEN, payload: isMobile });
  };

  const toggleMenu = () => {
    dispatch({ type: BITypes.TOGGLE_MENU });
  };

  const setActiveGroup = (reportGroup?: IReportGroup) => {
    dispatch({ type: BITypes.SET_GROUP, payload: reportGroup });
  };

  const setReport = (report: IReport) => {
    dispatch({ type: BITypes.SET_REPORT, payload: report });
  };

  const setAvaliableReportGroups = (reportGroups?: IReportGroup[]) => {
    dispatch({ type: BITypes.SET_GROUPS, payload: reportGroups });
  };

  const getReportConfig = async (
    workspaceId: string,
    reportId: string,
    upn?: string,
  ) => {
    dispatch({
      type: BITypes.SET_REPORT_CONFIG,
      payload: { embedUrl: undefined, accessToken: undefined },
    });
    dispatch({
      type: BITypes.SET_ISLOADING,
      payload: true,
    });
    const response = await api.post(`/powerbi`, {
      workspaceId,
      reportId,
      upn,
    });

    dispatch({
      type: BITypes.SET_ISLOADING,
      payload: false,
    });

    if (!response.data) return;

    dispatch({
      type: BITypes.SET_REPORT_CONFIG,
      payload: response.data,
    });
  };

  const actions: IActions = {
    setScreen,
    toggleMenu,
    setActiveGroup,
    setReport,
    setAvaliableReportGroups,
    getReportConfig,
  };

  return (
    <BIContext.Provider value={{ state, actions }}>
      {children}
    </BIContext.Provider>
  );
};

const useBI = () => React.useContext(BIContext);

export { useBI };
export default BIContextProvider;
